/**
 * KimiTopBar - Kimi-style top bar
 * 
 * Exact Kimi UI parity:
 * - Left: "Edit mode" dropdown, TC logo, Page title dropdown
 * - Right: Undo/Redo, Share, Publish button
 * - Clean, minimal design
 * - "Saved" indicator
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, Undo2, Redo2, Share2, Upload, Loader2, Check, Eye
} from 'lucide-react';
import { Button } from '../ui/button';

const KimiTopBar = ({
  pageName = 'Page',
  pageRoute = '/',
  hasChanges = false,
  saving = false,
  publishing = false,
  onSave,
  onPublish,
  onUndo,
  onRedo,
  onShare,
  onPreview,
  extraActions,
}) => {
  const navigate = useNavigate();
  
  return (
    <header 
      className="h-12 bg-zinc-950 border-b border-zinc-800/30 flex items-center justify-between px-4"
      data-testid="kimi-top-bar"
    >
      {/* Left side */}
      <div className="flex items-center gap-4">
        {/* Edit mode dropdown */}
        <button className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-[13px]">
          <span>Edit mode</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        
        {/* Divider */}
        <div className="h-4 w-px bg-zinc-800" />
        
        {/* Logo */}
        <div 
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/admin/page-builder')}
        >
          <div className="w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center">
            <span className="text-[10px] font-bold text-white">TC</span>
          </div>
          <span className="text-white text-[13px] font-medium">Trap Comix</span>
        </div>
        
        {/* Page selector */}
        <button className="flex items-center gap-1.5 text-white text-[13px] hover:text-zinc-300">
          <span>{pageName}</span>
          <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
        </button>
      </div>
      
      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Undo/Redo */}
        <div className="flex items-center gap-1 mr-2">
          <button
            onClick={onUndo}
            className="p-1.5 text-zinc-500 hover:text-white rounded transition-colors"
            title="Undo"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={onRedo}
            className="p-1.5 text-zinc-500 hover:text-white rounded transition-colors"
            title="Redo"
          >
            <Redo2 className="w-4 h-4" />
          </button>
        </div>
        
        {/* Divider */}
        <div className="h-4 w-px bg-zinc-800 mr-2" />
        
        {/* Share button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onShare}
          className="border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800 h-8 text-[12px]"
        >
          <Share2 className="w-3.5 h-3.5 mr-1.5" />
          Share
        </Button>
        
        {/* Extra actions slot */}
        {extraActions}
        
        {/* Preview as Visitor button */}
        {onPreview && (
          <Button
            variant="outline"
            size="sm"
            onClick={onPreview}
            className="border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800 h-8 text-[12px]"
            data-testid="preview-as-visitor-btn"
          >
            <Eye className="w-3.5 h-3.5 mr-1.5" />
            Preview
          </Button>
        )}
        
        {/* Publish button */}
        <Button
          onClick={onPublish}
          disabled={publishing}
          size="sm"
          className="bg-orange-500 hover:bg-orange-600 text-white h-8 text-[12px] px-4"
        >
          {publishing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
          ) : (
            <Upload className="w-3.5 h-3.5 mr-1.5" />
          )}
          Publish
        </Button>
        
        {/* Save indicator */}
        <div className="flex flex-col items-center ml-2 min-w-[40px]">
          {saving ? (
            <Loader2 className="w-3 h-3 animate-spin text-orange-500" />
          ) : hasChanges ? (
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
          ) : (
            <Check className="w-3 h-3 text-green-500" />
          )}
          <span className="text-[9px] text-zinc-500 mt-0.5">
            {saving ? 'Saving' : hasChanges ? 'Unsaved' : 'Saved'}
          </span>
        </div>
        
        {/* Build version stamp */}
        <div className="ml-2 pl-2 border-l border-zinc-800" data-testid="build-version-stamp">
          <span className="text-[9px] text-zinc-600 font-mono select-all" title={`Build: ${typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'}`}>
            v{typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__.slice(0, 16) : 'dev'}
          </span>
        </div>
      </div>
    </header>
  );
};

export default KimiTopBar;
